import { useContext, useEffect, useState } from 'react';

import DownloadIcon from '@mui/icons-material/Download';
import VideoLibrary from '@mui/icons-material/VideoLibrary';
import {
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Link,
    Stack,
    Typography
} from '@mui/material';
import Button from '@mui/material/Button';
import { useTranslation } from 'react-i18next';

import { RecordingDialogProps } from './types';
import { getRecordingArtifactURLs } from './util';
import { ClassContext } from '../../context/ClassContext';
import { useSchoolInfo } from '../../hooks/useSchoolInfo';

export const RecordingLinksDialog = (props: RecordingDialogProps) => {
    const { t } = useTranslation();
    const { open, downloading, recording, onClose } = props;
    const [status, setStatus] = useState<'loading' | 'loaded'>('loading');
    const [artifacts, setArtifacts] = useState<Record<string, string>>({});
    const { userUuid, classUuid } = useContext(ClassContext);
    const { data: schoolInfo } = useSchoolInfo();

    const handleClose = () => {
        onClose?.({}, 'backdropClick');
    };

    useEffect(() => {
        if (!open) {
            return;
        }

        if (schoolInfo?.uuid && userUuid && classUuid && recording?.recordingId) {
            const applyArtifacts = async () => {
                const artifactURLsByContentType = await getRecordingArtifactURLs(
                    schoolInfo?.uuid,
                    userUuid,
                    classUuid,
                    recording.recordingId
                );

                setArtifacts(artifactURLsByContentType ?? {});
                setStatus('loaded');
            };

            setStatus('loading');
            applyArtifacts();
        } else {
            setArtifacts({});
            setStatus('loaded');
        }
    }, [classUuid, open, recording, schoolInfo?.uuid, userUuid]);

    return (
        <Dialog onClose={handleClose} open={open}>
            <DialogTitle>
                {t(downloading ? 'lti:recordings.downloadmedia' : 'lti:recordings.external-link')}
                {status === 'loading' && <CircularProgress size={16} sx={{ marginLeft: '8px' }} />}
            </DialogTitle>

            <DialogContent>
                {status === 'loaded' && (
                    <>
                        {Object.keys(artifacts).length > 0 ? (
                            <Stack gap={1}>
                                {Object.entries(artifacts).map(([contents, url]) => (
                                    <Link
                                        key={contents}
                                        underline="hover"
                                        href={url}
                                        display="flex"
                                        alignItems="center"
                                    >
                                        {downloading ? <DownloadIcon /> : <VideoLibrary />}

                                        <Typography ml={1}>{t(`lti:recordings.mediatypes.${contents}`)}</Typography>
                                    </Link>
                                ))}
                            </Stack>
                        ) : (
                            <DialogContentText>{t('lti:recordings.nomedia')}</DialogContentText>
                        )}
                    </>
                )}
            </DialogContent>

            <DialogActions>
                <Button autoFocus onClick={() => handleClose()} variant="contained" color="primary">
                    {t('common:close')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
